'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { MapPin, Shield, Users, AlertTriangle, ChevronRight } from 'lucide-react';

const MapView = dynamic(() => import('./map-view'), { ssr: false });

interface ChopPoint {
  id: string;
  name: string;
  inn: string;
  lat: number;
  lng: number;
  rating: number;
  status: string;
  address: string;
}

export default function MarketMapPage() {
  const [data, setData] = useState<ChopPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, mapped: 0 });
  const [selected, setSelected] = useState<ChopPoint | null>(null);

  useEffect(() => {
    fetch('/api/map/chops')
      .then((r) => r.json())
      .then((json) => {
        // API возвращает {total, active, data[]} — раньше в стейт уходил весь объект → data.map падал
        const points = Array.isArray(json) ? json : (json.data ?? []);
        setData(points);
        setStats({
          total: json.total || 0,
          active: json.active || 0,
          mapped: points.length,
        });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--portal-bg))] text-[hsl(var(--portal-text))] font-sans">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-6">
          <Link href="/" className="hover:text-[hsl(var(--portal-accent))] transition-colors">Главная</Link>
          <span>/</span>
          <span className="text-[hsl(var(--portal-text))]">Карта рынка ЧОП</span>
        </div>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-mono text-3xl font-bold tracking-tight uppercase">Карта рынка ЧОП</h1>
            <p className="text-[hsl(var(--portal-text-muted))] mt-1">Рынок частной охраны России на карте</p>
          </div>
          <div className="flex gap-6 text-sm">
            <div className="text-center">
              <div className="font-mono text-2xl font-bold tabular-nums text-[hsl(var(--portal-text))]">15 927</div>
              <div className="text-[hsl(var(--portal-text-muted))] text-xs">ЧОП в реестре</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-2xl font-bold tabular-nums text-[var(--index)]">{stats.active.toLocaleString()}</div>
              <div className="text-[hsl(var(--portal-text-muted))] text-xs">с лицензией</div>
            </div>
            <div className="text-center">
              <div className="font-mono text-2xl font-bold tabular-nums text-[hsl(var(--portal-warn))]">{stats.mapped.toLocaleString()}</div>
              <div className="text-[hsl(var(--portal-text-muted))] text-xs">на карте</div>
            </div>
          </div>
        </div>

        <div className="relative rounded-none overflow-hidden border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))]">
          {loading ? (
            <div className="h-[600px] flex items-center justify-center text-[hsl(var(--portal-text-muted))]">Загрузка карты...</div>
          ) : (
            <MapView data={data} onSelect={setSelected} />
          )}
        </div>

        {selected && (
          <div className="mt-4 p-4 rounded-none border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))]">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-mono font-bold text-lg uppercase">{selected.name}</h3>
                <p className="font-mono text-sm text-[hsl(var(--portal-text-muted))] mt-1 tabular-nums">ИНН: {selected.inn}</p>
                <p className="text-sm text-[hsl(var(--portal-text-muted))]">{selected.address}</p>
              </div>
              <div className="text-right">
                <div className="font-mono text-2xl font-bold tabular-nums text-[var(--index)]">{selected.rating?.toFixed(1) || '—'}</div>
                <div className="text-xs text-[hsl(var(--portal-text-muted))]">рейтинг</div>
              </div>
            </div>
            <Link
              href={`/c/${selected.id}`}
              className="inline-flex items-center gap-1 mt-3 text-sm text-[hsl(var(--portal-accent))] hover:text-[hsl(var(--portal-accent-hover))] transition-colors"
            >
              Подробнее <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
